<template>
	<v-layout
		row
		wrap
		justify-center
	>
		<v-flex v-show="items.length > 0" xl8>
			<v-data-iterator
				ref="iterator"
				v-model="selectedData"
				:items="items"
				:pagination.sync="pagination"
				:search="search"
				:custom-sort="customSort"
				:rows-per-page-items="rowsPerPageItems"
				:custom-filter="filterItems"
				no-data-text="No articles available"
				no-results-text="No matching articles found"
				content-tag="v-layout"
				row
				wrap
			>
				<template
					v-if="!hideToolbar"
					slot="header"
				>
					<IteratorToolbar
						:extended="bulkAction"
						:sort-by="sortBy"
						:sort-name="sortName"
						:pagination="pagination"
						:initial-view="changeView"
						@search="search = $event"
						@change-view="changeView = $event"
						@sort="sort"
						@pagination="changeSortDirection"
					>
						<template v-if="!isArchive" slot="additional">
							<v-tooltip top>
								<v-btn
									v-if="$userCan('manage_reports') && !report.isArchivedOrParentArchived"
									slot="activator"
									:color="bulkAction ? 'error' : 'info'"
									icon
									class="mx-3"
									@click="bulkAction = !bulkAction"
								>
									<v-icon>{{ bulkAction ? 'close' : 'edit' }}</v-icon>
								</v-btn>
								<span>Bulk Actions</span>
							</v-tooltip>
						</template>
						<template v-if="!isArchive" slot="extended">
							<div>
								<v-checkbox
									v-model="selectAll"
									:indeterminate="indeterminateSelection()"
									:label="selectMessage"
									:disabled="items.length === 0"
									hide-details
									color="primary"
									@click.native="toggleSelect"
								/>
							</div>
							<v-divider
								vertical
								class="mx-2"
							/>
							<div class="body-1">With Selected: </div>
							<v-btn
								:disabled="selected.length < 1"
								flat
								color="error"
								@click="$store.set('articlesDeleteDialog/openArticleDeleteDialog!', selected)"
							>
								Delete
							</v-btn>
							<v-btn
								:disabled="selected.length < 1"
								flat
								color="success"
								@click="$store.set('articlesBulkDialog/openArticleBulkDialog!', {
									action: 'move',
									articles: selected,
								})"
							>
								Move
							</v-btn>
							<v-btn
								:disabled="selected.length < 1"
								flat
								color="success"
								@click="$store.set('articlesBulkDialog/openArticleBulkDialog!', {
									action: 'copy',
									articles: selected,
								})"
							>
								Copy
							</v-btn>
							<v-btn
								:disabled="selected.length < 1 || this.$store.get('categories').length === 0"
								flat
								color="success"
								@click="$store.set('articlesBulkDialog/openArticleBulkDialog!', {
									action: 'change-category',
									articles: selected,
								})"
							>
								Change Category
							</v-btn>
							<v-btn
								:disabled="selected.length < 1"
								flat
								color="success"
								@click="$store.set('articlesRefetchStatsDialog/openArticleRefetchStatsDialog!', selected)"
							>
								Refetch Stats
							</v-btn>
						</template>
					</IteratorToolbar>
					<v-progress-linear
						v-if="articlesLoading.changing"
						class="ma-0"
						indeterminate
					/>
				</template>
				<v-flex
					slot="item"
					slot-scope="props"
					:class="{ 'py-1': changeView !== 0 }"
					:sm6="changeView === 0"
					:md4="changeView === 0"
					:lg3="changeView === 0"
					xs12
				>
					<ArticleCard
						v-if="changeView === 0"
						:article="props.item"
						:hide-image-settings="hideImageSettings"
						:isArchive="isArchive"
						@set-image="$emit('set-image', $event)"
						@set-add-reprints="$emit('set-add-reprints', $event)"
						@set-project-image="$emit('set-project-image', $event)"
						@set-report-image="$emit('set-report-image', $event)"
						@edit="$emit('edit', $event)"
						@restore="$emit('restore', $event)"
					>
						<div
							v-if="bulkAction"
							slot="checkbox"
							@click="props.selected = !props.selected"
						>
							<v-checkbox
								:input-value="props.selected"
								hide-details
								color="primary"
								class="ma-0 pa-0"
							/>
						</div>
					</ArticleCard>
					<ArticleList
						v-else
						:article="props.item"
						:hide-image-settings="hideImageSettings"
						:isArchive="isArchive"
						@set-image="$emit('set-image', $event)"
						@set-project-image="$emit('set-project-image', $event)"
						@set-report-image="$emit('set-report-image', $event)"
						@edit="$emit('edit', $event)"
						@restore="$emit('restore', $event)"
					>
						<div
							v-if="bulkAction"
							slot="checkbox"
							@click="props.selected = !props.selected"
						>
							<v-checkbox
								:input-value="props.selected"
								hide-details
								color="primary"
								class="my-1 absolute--right"
							/>
						</div>
					</ArticleList>
				</v-flex>
			</v-data-iterator>
		</v-flex>
		<v-flex v-if="items.length === 0 && !report.isArchivedOrParentArchived && !hideContext && !isArchive" xs12>
			<ContextCard
				icon="arrow_upward"
			>
				<template slot="title">
					Add Articles to your Report.
				</template>
			</ContextCard>
		</v-flex>
		<v-flex v-else-if="items.length <= 6 && !report.isArchivedOrParentArchived && !hideContext && !isArchive" xs12>
			<ContextCard
				icon="info"
			>
				<template slot="title">
					Want to Add More?
				</template>
				<template slot="text">
					Click <span class="body-2">Add Articles</span>.
					Otherwise, you can view and share your final report by
					clicking <span class="body-2">Output Report</span> in the top right.
				</template>
			</ContextCard>
		</v-flex>
		<v-flex v-else-if="items.length === 0" xs12 class="text-xs-center">
			No articles available.
		</v-flex>
	</v-layout>
</template>

<script>
import IteratorMixin from '@/mixins/IteratorMixin';
import IteratorToolbar from '@/components/IteratorToolbar';
import ArticleCard from '@/components/Article/ArticleCard';
import ArticleList from '@/components/Article/ArticleList';
import ContextCard from '@/components/ContextCard';
import { sync } from 'vuex-pathify';

export default {
	components: {
		ArticleCard,
		ArticleList,
		IteratorToolbar,
		ContextCard,
	},
	mixins: [IteratorMixin],
	props: {
		items: { type: Array, default: null },
		selected: { type: Array, default: () => [] },
		hideImageSettings: { type: Boolean, default: false },
		initialSort: { type: Object, default: null },
		hideToolbar: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
		hideContext: { type: Boolean, default: false },
		isArchive: { type: Boolean, default: false },
	},
	data() {
		return {
			pagination: {
				sortBy: 'title',
				descending: false,
			},
			sortBy: [
				{ name: 'Title', value: 'title' },
				{ name: 'Date Added', value: 'id' },
				{ name: 'Date', value: 'date' },
				{ name: 'Author', value: 'author' },
				{ name: 'Publication', value: 'publicationName' },
				{ name: 'Category', value: 'categoryName' },
			],
			selectedData: [],
			selectAll: false,
			bulkAction: false,
			moveValue: null,
			cardStyle: 'cardStyle_articles',
			sortStorage: 'sort_articles',
			fuseOptions: {
				keys: [
					{ name: 'title', weight: 0.45 },
					{ name: 'author', weight: 0.1 },
					{ name: 'publicationName', weight: 0.1 },
					{ name: 'body', weight: 0.05 },
				],
				minMatchCharLength: 2,
				threshold: 0.4,
				distance: 10000,
				shouldSort: true,
			},
		};
	},
	created() {
		if (this.initialSort) {
			this.pagination = this.initialSort;
		}
	},
	computed: {
		report() {
			return this.$store.get('reports/currentReport');
		},
		selectMessage() {
			return (this.selectAll ? 'Deselect All' : 'Select All');
		},
		articlesLoading: sync('events/articlesLoading'),
	},
	watch: {
		selected(val) {
			this.selectedData = val;
		},
		selectedData(val) {
			this.$emit('selected', val);
		},
	},
	methods: {
		emitEvent(name, project, report, category, article) {
			if (!article) {
				this.$emit(name, project, report, category);
				return;
			}
			this.$emit(name, project, report, category, article);
		},
		toggleSelect() {
			const articles = this.$refs.iterator.filteredItems;
			if (this.selectAll === false) {
				this.selectedData = [];
			}
			else {
				this.selectedData = articles;
			}
		},
		indeterminateSelection() {
			if (this.selected.length === 0) {
				this.selectAll = false;
				return false;
			}
			if (this.selected.length === this.items.length) {
				this.selectAll = true;
				return false;
			}
			return true;
		},
		filterItems(items, search, filter) {
			return search === '' ? items : this.fuse.search(search);
		},
		customSort(items, index, isDescending) {
			if (index === null) return items;
			if (this.search !== '') {
				return items;
			}
			const sortedItems = items.sort((a, b) => {
				// Sort by importance
				if (a.important < b.important) return -1;
				if (a.important > b.important) return 1;

				let sortA = a[index];
				let sortB = b[index];

				if (isDescending) {
					[sortA, sortB] = [sortB, sortA];
				}

				// Check if both are numbers
				// eslint-disable-next-line
				if (!isNaN(sortA) && !isNaN(sortB)) {
					return sortA - sortB;
				}

				// Check if both cannot be evaluated
				if (sortA === null && sortB === null) {
					return 0;
				}

				[sortA, sortB] = [sortA, sortB]
					.map(s => (
						(s || '').toString().toLocaleLowerCase()
					));

				if (sortA > sortB) return 1;
				if (sortA < sortB) return -1;

				return 0;
			});

			const sortedNewItems = [];
			// eslint-disable-next-line
			for (const o of sortedItems) {
				if (o.justAdded && !o.important) {
					sortedNewItems.unshift(o);
				}
				else {
					sortedNewItems.push(o);
				}
			}
			return sortedNewItems;
		},
	},
};
</script>

<style lang="scss" scoped>
.absolute--right {
	position: absolute;
	top: 0;
	right: 0;
}
.v-input--checkbox {
	margin: 0;
}
.v-divider--vertical {
	min-height: 30px;
}
</style>
